/*
 * LOCAL-ONLY: This file is a local-only addition to fluxer_app and will never exist upstream.
 * It implements selection mode for the message relocation feature, coordinating with the fork's
 * POST /api/v1/channels/relocate-messages (+ /undo) and GET /channels/relocate-log routes.
 *
 * The flow (relocation-plan.md F1, F2, F4, F5, F12, F15): pick a range and a destination, count it on the server
 * (dry run), confirm, then move it in batches of 500 with progress and a stop after any batch. The server
 * re-indexes search and tells every client (MESSAGE_DELETE_BULK on the source); this store also updates the local
 * lists at once. Recent moves can be undone (per group) and a move that stopped part-way resumed.
 *
 * Lines to check on upstream merge: none — exclude this file entirely from any upstream sync.
 */

// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	errorText,
	NOTHING_MOVED,
	RelocationStrings,
	requestFailedText,
	type SelectionCount,
} from '@app/features/channel/state/RelocationStrings';

export type {SelectionCount};

import {noteRelocationRemoval, refillWindowAfterRelocation} from '@app/features/channel/state/RelocationRefill';
import MemberList from '@app/features/member/state/MemberList';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import MessagingMessages from '@app/features/messaging/state/MessagingMessages';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {http} from '@app/features/platform/transport/RestTransport';
import Dimension from '@app/features/ui/state/Dimension';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {MessageStates} from '@fluxer/constants/src/ChannelConstants';
import {makeAutoObservable, runInAction} from 'mobx';

/** The server's per-request maximum (MessageRelocationRepository MAX_RELOCATION_MESSAGES). */
export const RELOCATION_BATCH_SIZE = 500;

interface RelocateResponse {
	movedCount: number;
	nextStartMessageId?: string | null;
	logId?: string | null;
	groupId?: string | null;
	fileFailures?: number;
	count?: number;
	notMovable?: number;
}

interface UndoResponse {
	movedCount: number;
	remaining: number;
	logId: string | null;
	undoneLogId: string | null;
	fileFailures: number;
}

// LOCAL-ONLY: shape of GET /channels/relocate-log?grouped=true entries — exclude from upstream sync.
export interface RelocateLogEntry {
	logId: string;
	performedBy: {
		id: string;
		displayName: string | null;
	};
	sourceChannel: {
		id: string;
		name: string | null;
	};
	destChannel: {
		id: string;
		name: string | null;
	};
	startMessageId: string;
	endMessageId: string;
	movedCount: number;
	createdAt: string;
	state?: 'started' | 'done';
	kind?: 'move' | 'undo';
	groupId?: string;
	batches?: number;
	undone?: 'none' | 'partly' | 'all';
	unfinishedLogId?: string | null;
	groupStartMessageId?: string | null;
	groupEndMessageId?: string | null;
	fileFailures?: number;
}

export interface RelocationConfirm {
	count: number;
	notMovable: number;
}

export interface RelocationResult {
	movedCount: number;
	destChannelId: string;
	/** The user stopped the batches before the end. */
	stopped: boolean;
	/** The total from the count, for "Stopped after N of M". */
	total: number | null;
	fileFailures: number;
	/** A move back (undo), named after the channel the messages returned to. */
	undo: boolean;
}

interface MoveRun {
	sourceChannelId: string;
	destChannelId: string;
	startMessageId: string;
	endMessageId: string;
	total: number | null;
	groupId?: string | null;
	resumeLogId?: string | null;
}

const RECENT_LOG_LIMIT = 5;

/**
 * The server's own message for an API error (`body.message`, translated server-side), else a plain status line.
 * Before relocation-plan.md F2 the panel showed `[400] POST /channels/relocate-messages`.
 */
export function relocationErrorText(error: unknown): string {
	if (error && typeof error === 'object') {
		const body = (error as {body?: unknown}).body;
		if (body && typeof body === 'object') {
			const message = (body as {message?: unknown}).message;
			if (typeof message === 'string' && message.length > 0) return errorText(message);
		}
		const status = (error as {status?: unknown}).status;
		if (typeof status === 'number') return errorText(requestFailedText(status));
	}
	return errorText(error instanceof Error ? error.message : 'unknown');
}

class SelectMode {
	isActive = false;
	// LOCAL-ONLY: isPanelOpen is phone-width only — whether the relocate panel is open over the selection bar
	// (MobileRelocationLayer). Desktop ignores it and renders the panel whenever isActive && channelId matches.
	// Exclude from upstream sync.
	isPanelOpen = false;
	channelId: string | null = null;
	anchorId: string | null = null;
	headId: string | null = null;
	// LOCAL-ONLY: destination server for the move. Either a guild ID, the panel's DMs sentinel
	// ('dms'), or null meaning "the user has not picked one yet", in which case the panel falls
	// back to the channel's own context. Lives here rather than in panel component state so it
	// survives the mobile close/reopen cycle (tap messages with the panel shut, reopen to submit);
	// it is in-memory only and is gone on reload. Exclude from upstream sync.
	destGuildId: string | null = null;
	destChannelId: string | null = null;
	submitting = false;
	counting = false;
	/** The dry-run answer waiting for the user's confirmation (F1, F5). */
	confirm: RelocationConfirm | null = null;
	/** Batches in flight: moved so far and the counted total. */
	progress: {moved: number; total: number | null; undo: boolean} | null = null;
	stopRequested = false;
	lastError: string | null = null;
	result: RelocationResult | null = null;
	/**
	 * LOCAL-ONLY: the server's exact count of the selected range (a dry run without a destination), asked for once
	 * both ends are set and again whenever either end changes; null while the range is incomplete. Replaces the
	 * count of loaded messages with its "+" when an end was outside them. Exclude from upstream sync.
	 */
	selectionCount: SelectionCount | null = null;
	selectionCountRequest = 0;
	/** A log entry whose Undo is waiting for confirmation. */
	undoConfirm: RelocateLogEntry | null = null;
	/** LOCAL-ONLY (section 17): the selection the window was last refilled for (once per selection). */
	private selectionRefilledFor: string | null = null;
	recentLog: Array<RelocateLogEntry> = [];
	logLoading = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get startMessageId(): string | null {
		if (this.anchorId == null) return null;
		if (this.headId == null) return this.anchorId;
		return BigInt(this.anchorId) <= BigInt(this.headId) ? this.anchorId : this.headId;
	}

	get endMessageId(): string | null {
		if (this.anchorId == null) return null;
		if (this.headId == null) return this.anchorId;
		return BigInt(this.anchorId) >= BigInt(this.headId) ? this.anchorId : this.headId;
	}

	get busy(): boolean {
		return this.submitting || this.counting;
	}

	get canSubmit(): boolean {
		return this.isActive && this.anchorId != null && this.destChannelId != null && !this.busy;
	}

	/**
	 * LOCAL-ONLY (relocation parity, 2026-10-30): relocation starts only from a message's "Relocate messages" (menu
	 * only: no header buttons, no selection switch). Selection is on while relocation is open, with that message as
	 * the start, as in Obyrus. On desktop the side panel shows at once; at phone width the selection bar shows and the
	 * panel opens from its Relocate button (Obyrus' bar and sheet). The destination is kept when the channel is the one
	 * already selected.
	 */
	startFrom(channelId: string, messageId: string): void {
		if (this.channelId !== channelId) {
			this.destGuildId = null;
			this.destChannelId = null;
		}
		this.channelId = channelId;
		this.isActive = true;
		this.setAnchor(messageId);
		if (MobileLayout.enabled) {
			this.isPanelOpen = false;
		} else {
			this.openPanel();
		}
		void this.fetchRecentLog();
	}

	/**
	 * LOCAL-ONLY (relocation gestures, 2026-10-30): a click or tap on a message row while selecting, as Obyrus'
	 * session.tap: the first sets the start, the next the end, a third starts over. Shift+click sets the end directly
	 * (relocation-plan.md F10). Other buttons change nothing (the row's own actions are off while selecting).
	 */
	selectFromClick(messageId: string, click: {button: number; shiftKey: boolean}): void {
		if (click.button !== 0) return;
		if (click.shiftKey && this.anchorId != null) {
			this.setHead(messageId);
			return;
		}
		if (this.anchorId == null || this.headId != null) {
			this.setAnchor(messageId);
		} else {
			this.setHead(messageId);
		}
	}

	/**
	 * LOCAL-ONLY (relocation gestures, 2026-10-30): whether the selection owns this row's gestures: a row in the channel
	 * being selected in, except an unsent one (sending or failed), which keeps its own gestures and can't be picked, as
	 * Obyrus' RelocationSelectable leaves rows that aren't sent alone.
	 */
	selectsRow(channelId: string, state: string): boolean {
		return (
			this.isActive && this.channelId === channelId && state !== MessageStates.SENDING && state !== MessageStates.FAILED
		);
	}

	/**
	 * LOCAL-ONLY (relocation gestures): set the end directly (a long-press while selecting, Obyrus' extend); without
	 * a start yet, it becomes the start.
	 */
	extendTo(messageId: string): void {
		if (this.anchorId == null) {
			this.setAnchor(messageId);
			return;
		}
		this.setHead(messageId);
	}

	deactivate(): void {
		this.isActive = false;
		this.selectionCount = null;
		this.selectionCountRequest++;
		this.isPanelOpen = false;
		this.channelId = null;
		this.anchorId = null;
		this.headId = null;
		this.destGuildId = null;
		this.destChannelId = null;
		this.submitting = false;
		this.counting = false;
		this.confirm = null;
		this.progress = null;
		this.stopRequested = false;
		this.lastError = null;
		this.result = null;
		this.undoConfirm = null;
	}

	// LOCAL-ONLY: mobile panel visibility — decoupled from isActive — exclude from upstream sync.
	openPanel(): void {
		this.isPanelOpen = true;
		// LOCAL-ONLY: desktop side-panel mutual exclusivity. On desktop this panel and the
		// Members panel share the single ChannelViewScaffold sidePanel slot (Members wins the
		// ternary), so opening this one has to close Members or the panel never appears.
		// Mobile renders through a separate fixed overlay and is deliberately untouched.
		if (!MobileLayout.enabled && MemberList.isMembersOpen) {
			MemberList.toggleMembers();
		}
	}

	closePanel(): void {
		this.isPanelOpen = false;
	}

	setAnchor(messageId: string): void {
		this.anchorId = messageId;
		this.headId = null;
		this.result = null;
		this.lastError = null;
		this.confirm = null;
		this.refreshSelectionCount();
	}

	setHead(messageId: string): void {
		this.headId = messageId;
		this.confirm = null;
		this.refreshSelectionCount();
	}

	/**
	 * LOCAL-ONLY: ask the server how many messages the range holds (the confirmation's count, without a
	 * destination). An answer to an older range is ignored. Exclude from upstream sync.
	 */
	refreshSelectionCount(): void {
		const request = ++this.selectionCountRequest;
		const {channelId, anchorId, headId, startMessageId, endMessageId} = this;
		if (channelId == null || anchorId == null || headId == null || startMessageId == null || endMessageId == null) {
			this.selectionCount = null;
			return;
		}
		this.selectionCount = {state: 'counting'};
		void (async () => {
			try {
				const response = await http.post<RelocateResponse>('/channels/relocate-messages', {
					body: {sourceChannelId: channelId, startMessageId, endMessageId, dryRun: true},
				});
				const count = response.body.count ?? 0;
				runInAction(() => {
					if (request === this.selectionCountRequest) this.selectionCount = {state: 'ready', count};
				});
				if (request === this.selectionCountRequest) {
					this.refillWhenSelectionSpansUnshown(channelId, startMessageId, endMessageId, count);
				}
			} catch (error) {
				runInAction(() => {
					if (request === this.selectionCountRequest) {
						this.selectionCount = {state: 'error', message: relocationErrorText(error)};
					}
				});
			}
		})();
	}

	/**
	 * LOCAL-ONLY (relocation-plan.md section 17): a selection never spans unshown messages. When the server counts more
	 * messages in the range than are loaded between its ends, the open channel's window is fetched again (once per
	 * selection). Exclude from upstream sync.
	 */
	private refillWhenSelectionSpansUnshown(channelId: string, start: string, end: string, count: number): void {
		const key = `${channelId}:${start}:${end}`;
		if (this.selectionRefilledFor === key || SelectedChannel.currentChannelId !== channelId) return;
		let shown = 0;
		MessagingMessages.getMessages(channelId).forEachBuffered((message) => {
			if (!/^\d+$/.test(message.id)) return;
			const id = BigInt(message.id);
			if (id >= BigInt(start) && id <= BigInt(end)) shown++;
		});
		if (count <= shown) return;
		this.selectionRefilledFor = key;
		void refillWindowAfterRelocation(channelId);
	}

	reset(): void {
		this.anchorId = null;
		this.headId = null;
		this.result = null;
		this.lastError = null;
		this.confirm = null;
		this.refreshSelectionCount();
	}

	// LOCAL-ONLY: changing the destination server invalidates any channel already picked under the
	// previous one, so the channel is dropped on a real change only — re-setting the same value
	// (e.g. the panel re-asserting its default on mount) leaves the selection intact.
	// Exclude from upstream sync.
	setDestGuildId(guildId: string | null): void {
		if (this.destGuildId === guildId) {
			return;
		}
		this.destGuildId = guildId;
		this.destChannelId = null;
		this.confirm = null;
	}

	setDestChannelId(channelId: string | null): void {
		this.destChannelId = channelId;
		this.confirm = null;
	}

	// LOCAL-ONLY: relocate audit log fetch, one entry per selection (grouped) — exclude from upstream sync.
	async fetchRecentLog(): Promise<void> {
		runInAction(() => {
			this.logLoading = true;
		});
		try {
			const response = await http.get<Array<RelocateLogEntry>>('/channels/relocate-log', {
				query: {limit: RECENT_LOG_LIMIT, grouped: 'true'},
			});
			runInAction(() => {
				this.recentLog = response.body;
				this.logLoading = false;
			});
		} catch {
			runInAction(() => {
				this.logLoading = false;
			});
		}
	}

	/** Step 1 (F1, F5): count the range on the server and ask for confirmation. Nothing moves yet. */
	async requestRelocate(): Promise<void> {
		const {channelId, startMessageId, endMessageId, destChannelId} = this;
		if (
			!this.canSubmit ||
			channelId == null ||
			startMessageId == null ||
			endMessageId == null ||
			destChannelId == null
		) {
			return;
		}
		runInAction(() => {
			this.counting = true;
			this.lastError = null;
			this.result = null;
			this.confirm = null;
		});
		try {
			const response = await http.post<RelocateResponse>('/channels/relocate-messages', {
				body: {sourceChannelId: channelId, destChannelId, startMessageId, endMessageId, dryRun: true},
			});
			runInAction(() => {
				this.confirm = {count: response.body.count ?? 0, notMovable: response.body.notMovable ?? 0};
				this.counting = false;
			});
		} catch (error) {
			runInAction(() => {
				this.lastError = relocationErrorText(error);
				this.counting = false;
			});
		}
	}

	cancelConfirm(): void {
		this.confirm = null;
	}

	/** Step 2: the confirmed move, in batches. */
	async confirmRelocate(): Promise<void> {
		await this.submit();
	}

	/** Stop after the batch in flight (the server never stops a batch half-way on purpose). */
	requestStop(): void {
		this.stopRequested = true;
	}

	async submit(): Promise<void> {
		const {channelId, startMessageId, endMessageId, destChannelId} = this;
		if (
			!this.canSubmit ||
			channelId == null ||
			startMessageId == null ||
			endMessageId == null ||
			destChannelId == null
		) {
			return;
		}
		await this.runMove({
			sourceChannelId: channelId,
			destChannelId,
			startMessageId,
			endMessageId,
			total: this.confirm?.count ?? null,
		});
	}

	/** Resume a selection whose batch stopped part-way (state "started"), then move whatever is left of it. */
	async resume(entry: RelocateLogEntry): Promise<void> {
		if (this.busy || entry.unfinishedLogId == null) return;
		await this.runMove({
			sourceChannelId: entry.sourceChannel.id,
			destChannelId: entry.destChannel.id,
			startMessageId: entry.groupStartMessageId ?? entry.startMessageId,
			endMessageId: entry.groupEndMessageId ?? entry.endMessageId,
			total: null,
			groupId: entry.groupId ?? null,
			resumeLogId: entry.unfinishedLogId,
		});
	}

	requestUndo(entry: RelocateLogEntry): void {
		this.undoConfirm = entry;
		this.lastError = null;
		this.result = null;
	}

	cancelUndo(): void {
		this.undoConfirm = null;
	}

	/** Undo a whole selection: the server moves the group back one batch per call (F15). */
	async confirmUndo(): Promise<void> {
		const entry = this.undoConfirm;
		if (!entry || this.busy || entry.groupId == null) return;
		runInAction(() => {
			this.undoConfirm = null;
			this.submitting = true;
			this.lastError = null;
			this.result = null;
			this.progress = {moved: 0, total: null, undo: true};
		});
		let moved = 0;
		let fileFailures = 0;
		try {
			for (;;) {
				const response = await http.post<UndoResponse>('/channels/relocate-messages/undo', {
					body: {groupId: entry.groupId},
				});
				moved += response.body.movedCount;
				fileFailures += response.body.fileFailures;
				runInAction(() => {
					this.progress = {moved, total: null, undo: true};
				});
				if (response.body.remaining <= 0) break;
			}
			// Both channels changed under the cached lists: refetch them on open. The moved-back messages must not stay
			// hidden in the channel they return to (relocation-plan.md bug 4).
			this.forgetChannel(entry.sourceChannel.id);
			this.forgetChannel(entry.destChannel.id);
			runInAction(() => {
				this.result = {
					movedCount: moved,
					destChannelId: entry.sourceChannel.id,
					stopped: false,
					total: null,
					fileFailures,
					undo: true,
				};
			});
		} catch (error) {
			runInAction(() => {
				this.lastError = relocationErrorText(error);
			});
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.progress = null;
			});
			// LOCAL-ONLY: the range changed (messages moved): count the selection again. Exclude from upstream sync.
			this.refreshSelectionCount();
			void this.fetchRecentLog();
		}
	}

	private async runMove(run: MoveRun): Promise<void> {
		runInAction(() => {
			this.submitting = true;
			this.lastError = null;
			this.result = null;
			this.confirm = null;
			this.stopRequested = false;
			this.progress = {moved: 0, total: run.total, undo: false};
		});
		let next: string | null = run.startMessageId;
		let groupId = run.groupId ?? null;
		let moved = 0;
		let fileFailures = 0;
		let stopped = false;
		try {
			if (run.resumeLogId != null) {
				const resumed = await http.post<RelocateResponse>('/channels/relocate-messages', {
					body: {
						sourceChannelId: run.sourceChannelId,
						destChannelId: run.destChannelId,
						startMessageId: run.startMessageId,
						endMessageId: run.endMessageId,
						resumeLogId: run.resumeLogId,
					},
				});
				moved += resumed.body.movedCount;
				fileFailures += resumed.body.fileFailures ?? 0;
				groupId = resumed.body.groupId ?? groupId;
				// The resumed batch's ids are not all known here: refetch both lists.
				this.forgetChannel(run.sourceChannelId);
				this.forgetChannel(run.destChannelId);
			}
			while (next != null && BigInt(next) <= BigInt(run.endMessageId)) {
				const batchStart: string = next;
				const response = await http.post<RelocateResponse>('/channels/relocate-messages', {
					body: {
						sourceChannelId: run.sourceChannelId,
						destChannelId: run.destChannelId,
						startMessageId: batchStart,
						endMessageId: run.endMessageId,
						limit: RELOCATION_BATCH_SIZE,
						...(groupId != null ? {groupId} : {}),
					},
				});
				const body = response.body;
				groupId = body.groupId ?? groupId;
				next = body.nextStartMessageId ?? null;
				// Every local state mutation is gated on the server having actually moved something: the response is
				// the only authority on what moved, not the locally computed id range.
				if (body.movedCount > 0) {
					const batchEnd = next != null ? (BigInt(next) - 1n).toString() : run.endMessageId;
					this.applyLocally(run.sourceChannelId, run.destChannelId, batchStart, batchEnd);
				}
				moved += body.movedCount;
				fileFailures += body.fileFailures ?? 0;
				runInAction(() => {
					this.progress = {moved, total: run.total, undo: false};
				});
				if (next != null && this.stopRequested) {
					stopped = true;
					break;
				}
			}
			runInAction(() => {
				this.result = {
					movedCount: moved,
					destChannelId: run.destChannelId,
					stopped,
					total: run.total,
					fileFailures,
					undo: false,
				};
				// F12: a finished move clears the selection, so a second click can't send an empty range.
				if (moved > 0 && !stopped) {
					this.anchorId = null;
					this.headId = null;
				}
			});
		} catch (error) {
			runInAction(() => {
				this.lastError = relocationErrorText(error);
				if (moved > 0) {
					this.result = {
						movedCount: moved,
						destChannelId: run.destChannelId,
						stopped: true,
						total: run.total,
						fileFailures,
						undo: false,
					};
				}
			});
		} finally {
			runInAction(() => {
				this.submitting = false;
				this.progress = null;
				this.stopRequested = false;
			});
			// LOCAL-ONLY: the range changed (messages moved): count the selection again. Exclude from upstream sync.
			this.refreshSelectionCount();
			void this.fetchRecentLog();
		}
	}

	/**
	 * The local half of one moved batch: drop the loaded messages of the batch's id range from the source (the same
	 * path as gateway MESSAGE_DELETE_BULK, so scroll position is kept), guard them against a fetched page bringing
	 * them back, and drop the destination's cached list so its next open refetches with destination-scoped URLs.
	 */
	private applyLocally(sourceChannelId: string, destChannelId: string, start: string, end: string): void {
		const startBigInt = BigInt(start);
		const endBigInt = BigInt(end);
		const idsToRemove: Array<string> = [];
		// forEachBuffered (not forEach) walks the before/after buffers too, not just the visible window.
		MessagingMessages.getMessages(sourceChannelId).forEachBuffered((message) => {
			const mid = BigInt(message.id);
			if (mid >= startBigInt && mid <= endBigInt) {
				idsToRemove.push(message.id);
			}
		});
		MessagingMessages.markMovedIds(sourceChannelId, idsToRemove);
		// LOCAL-ONLY (relocation-plan.md bug 4): the destination may be a channel these messages left
		// earlier in this session (moving them back). Its guard must stop hiding them there.
		MessagingMessages.unmarkMovedIds(destChannelId, idsToRemove);
		ChannelMessages.clear(destChannelId);
		Dimension.forgetChannelDimensions(destChannelId);
		if (idsToRemove.length > 0) {
			noteRelocationRemoval(sourceChannelId, idsToRemove);
			MessagingMessages.handleMessageDeleteBulk({ids: idsToRemove, channelId: sourceChannelId});
		}
		// LOCAL-ONLY (relocation-plan.md section 17): the open channel's window is fetched again, so the move leaves no
		// gap and a short window takes the next messages. Exclude from upstream sync.
		void refillWindowAfterRelocation(sourceChannelId);
	}

	private forgetChannel(channelId: string): void {
		// LOCAL-ONLY (relocation-plan.md section 17): the open channel keeps its list (dropping it would blank the view)
		// and is fetched again around what is loaded, so messages an Undo or Resume brings back into it are shown where
		// they belong instead of leaving a gap that a later selection would silently span. Exclude from upstream sync.
		if (SelectedChannel.currentChannelId === channelId) {
			MessagingMessages.forgetMovedIds(channelId);
			void refillWindowAfterRelocation(channelId);
			return;
		}
		MessagingMessages.forgetMovedIds(channelId);
		ChannelMessages.clear(channelId);
		Dimension.forgetChannelDimensions(channelId);
	}
}

export {NOTHING_MOVED, RelocationStrings};
export default new SelectMode();
